<template>
    <div class="chat-wrap" :class="{ active: isVisible, resizing: isResizing }" :style="{ width: `${chatWidth}px` }">
        <div v-if="isVisible" class="chat-resize-handle" @mousedown="startResize"></div>
        <div v-if="isResizing" class="chat-resize-overlay"></div>
        <iframe
            v-if="token"
            v-show="isVisible"
            ref="aiChatFrame"
            :src="chatOrigin"
            width="100%" height="100%"
            frameborder="0"
        ></iframe>
    </div>
</template>
<script>
import r from "@/js/route";
import httpClient from '@/js/services/http-client';
export default {
    name: "AIChat",
    props: {
        isVisible: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            chatUrl: import.meta.env.VITE_AI_CHAT_URL,
            user: null,
            isResizing: false,
            chatWidth: 420,
            chatMinWidth: 420,
            chatMaxWidth: 770,
            resizeStartX: 0,
            resizeStartWidth: 420,
            // The chat reports aiChatReady once it can take a message; a renewed token reloads the iframe
            chatReady: false,
            pendingMessage: null,
            tokenRefresh: null,
        }
    },
    computed: {
        token() {
            return this.$root.user.token;
        },
        chatOrigin() {
            const url =  new URL(this.chatUrl);
            url.searchParams.set('token', this.token);
            return url.toString();
        },
    },
    watch: {
        chatOrigin() {
            this.chatReady = false;
        },
        isVisible(value) {
            if (value) {
                this.normalizeWidth();
                this.ensureFreshToken();
            } else {
                this.stopResize();
            }
        },
    },
    mounted() {
        window.addEventListener('resize', this.normalizeWidth);
        window.addEventListener('message', this.onChatMessage);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.normalizeWidth);
        window.removeEventListener('message', this.onChatMessage);
        this.stopResize();
    },
    methods: {
        isTokenExpired(token) {
            const payload = token?.split('.')[1];
            if (!payload) {
                return true;
            }

            try {
                const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
                return !exp || Date.now() >= exp * 1000;
            } catch (e) {
                return true;
            }
        },
        ensureFreshToken() {
            if (!this.isTokenExpired(this.token)) {
                return Promise.resolve();
            }

            // Opening the chat and starting a health-check both renew it: one request is enough
            if (!this.tokenRefresh) {
                this.tokenRefresh = this.$axios.post(r('getAuthUser'))
                    .then(({ data }) => {
                        this.chatReady = false;
                        this.$root.user.token = data.token;
                    })
                    .finally(() => {
                        this.tokenRefresh = null;
                    });
            }

            return this.tokenRefresh;
        },
        onChatMessage(event) {
            if (!this.$refs.aiChatFrame || event.source !== this.$refs.aiChatFrame.contentWindow) {
                return;
            }

            if (event.data?.type === 'toggleAIChat') {
                this.$emit('toggle');
            } else if (event.data?.type === 'aiChatReady') {
                this.chatReady = true;
                this.sendPendingMessage();
            }
        },
        // target: { subject: 'dsp-endpoint' | 'ssp-endpoint' | 'ssp-deal' | 'dsp-deal', id, name, dealId (deals only) }
        async startHealthCheck({ subject, id, name, dealId }) {
            await this.ensureFreshToken();
            this.pendingMessage = { type: 'startHealthCheck', subject, id, name, dealId };
            this.sendPendingMessage();
        },
        sendPendingMessage() {
            if (!this.chatReady || !this.pendingMessage) {
                return;
            }

            this.$refs.aiChatFrame.contentWindow.postMessage(this.pendingMessage, new URL(this.chatUrl).origin);
            this.pendingMessage = null;
        },
        startResize(event) {
            if (event.button !== 0) {
                return;
            }

            event.preventDefault();

            this.isResizing = true;
            this.resizeStartX = event.clientX;
            this.resizeStartWidth = this.chatWidth;
            document.body.classList.add('chat-resizing');
            window.addEventListener('mousemove', this.resize);
            window.addEventListener('mouseup', this.stopResize);
        },
        resize(event) {
            const nextWidth = this.resizeStartWidth + this.resizeStartX - event.clientX;
            this.chatWidth = this.getConstrainedWidth(nextWidth);
        },
        stopResize() {
            this.isResizing = false;
            document.body.classList.remove('chat-resizing');
            window.removeEventListener('mousemove', this.resize);
            window.removeEventListener('mouseup', this.stopResize);
        },
        normalizeWidth() {
            this.chatWidth = this.getConstrainedWidth(this.chatWidth);
        },
        getConstrainedWidth(width) {
            const maxWidth = Math.max(this.chatMinWidth, Math.min(this.chatMaxWidth, window.innerWidth - 20));

            return Math.min(Math.max(width, this.chatMinWidth), maxWidth);
        },

        auth() {
            this.$root.userPromise.then(user => {
                this.fetchToken().then(token =>  this.token = token)
            }).catch(err => {
                console.log(err);
            })
        },

        async fetchToken() {
          const resp =  await httpClient.post(r('token'));
          return resp.data.token;
        }
    },

}
</script>



<style lang="scss">
.chat-wrap {
    position: fixed;
    top: 0;
    bottom: 0;
    right: -100%;
    width: 420px;
    min-width: 420px;
    max-width: min(770px, calc(100vw - 20px));
    overflow: hidden;
    background: #fff;
    z-index: 10002;
    transition: right 400ms;
    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.20)) drop-shadow(0 2px 2px rgba(0, 0, 0, 0.12)) drop-shadow(0 0 2px rgba(0, 0, 0, 0.14));

    &.active {
        right: 0;
    }

    .chat-resize-handle {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 8px;
        cursor: ew-resize;
        z-index: 3;
    }

    .chat-resize-overlay {
        position: absolute;
        inset: 0;
        cursor: ew-resize;
        z-index: 2;
    }
}

body.chat-resizing {
    cursor: ew-resize;
    user-select: none;
}
</style>
